import {
  DestinationEntityRecordStatus,
  IMigratedDataSummary,
  SkippedDestinationEntityRecord,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
  type ISourceEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  PatientRelationshipType,
  type IBasePatient,
  type IHasPrimaryContact,
  type IPatient,
  type IPatientRelationship,
  type IsPrimaryContact,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  FirestoreMigrate,
  PrimaryKey,
  asDocRef,
  getError,
  isSameRef,
  multiMap,
  resolveSequentially,
  safeCombineLatest,
  toNamedDocument,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { DestinationEntity } from '../../../destination/destination-entity';
import {
  ExactPatient,
  PatientSourceEntity,
} from '../../source/entities/patient';
import {
  BasePatientRelationshipDestinationEntity,
  type IPatientRelationshipJobData as IPatientRelationshipBaseJobData,
} from '../../../destination/entities/patient-relationship';
import { PatientDestinationEntity } from './patient';
import { type Observable, of } from 'rxjs';
import { TranslationMapHandler } from '../../../translation-map';
import {
  Patient,
  PatientRelationship,
} from '@principle-theorem/principle-core';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { uniqWith } from 'lodash';

export const PATIENT_RELATIONSHIP_RESOURCE_ENTITY = DestinationEntity.init({
  metadata: {
    key: 'patientRelationships',
    label: 'Patient Relationships',
    description: '',
  },
});

export interface IPatientRelationshipDestinationRecord {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  patientRefs: DocumentReference<IPatient>[];
}

export type IPatientRelationshipJobData =
  IPatientRelationshipBaseJobData<ExactPatient>;

export interface IPatientMigrationData {
  sourcePatientId: string;
  patientRef: DocumentReference<IPatient>;
  primaryContacts: (IHasPrimaryContact & {
    patientRef: DocumentReference<IPatient>;
  })[];
  relationships: {
    patientRef: DocumentReference<IPatient>;
    relation: IPatientRelationship;
    // isPrimaryContact?: boolean;
  }[];
}

export class PatientRelationshipDestinationEntity extends BasePatientRelationshipDestinationEntity<
  ExactPatient,
  IPatientRelationshipDestinationRecord,
  IPatientMigrationData
> {
  destinationEntity = PATIENT_RELATIONSHIP_RESOURCE_ENTITY;

  patientSourceEntity = new PatientSourceEntity();
  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  getMigratedData$(
    record: IDestinationEntityRecord<IPatientRelationshipDestinationRecord>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return safeCombineLatest(
      record.data.patientRefs.map((patientRef) => Firestore.getDoc(patientRef))
    ).pipe(
      multiMap((patient) => ({
        label: 'Patient',
        data: patient,
      }))
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientRelationshipJobData
  ): Promise<
    | IPatientMigrationData
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients.getSourceRecordId(
        data.sourcePatient.data
      );
      const sourcePatientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!sourcePatientRef) {
        throw new Error(`Couldn't resolve patient`);
      }

      if (!data.sourcePatient.data.family_id) {
        return this.buildSkippedResponse(data);
      }

      const familyGroup = await this.sourceEntities.patients.filterRecords(
        migration,
        'familyId',
        data.sourcePatient.data.family_id
      );

      if (familyGroup.length <= 1) {
        return this.buildSkippedResponse(data);
      }

      const results: IPatientMigrationData = {
        sourcePatientId: sourcePatientId,
        patientRef: sourcePatientRef,
        primaryContacts: [],
        relationships: [],
      };

      // const sourcePatientIsHead = data.sourcePatient.data.is_head_of_family;

      await resolveSequentially(familyGroup, async (relatedPatientRecord) => {
        const relatedPatientId = this.sourceEntities.patients.getSourceRecordId(
          relatedPatientRecord.data
        );
        const relatedPatientRef = await translationMap.getDestination<IPatient>(
          relatedPatientId,
          PATIENT_RESOURCE_TYPE
        );
        if (isSameRef(sourcePatientRef, relatedPatientRef)) {
          return;
        }

        if (!relatedPatientRef) {
          throw new Error(
            `Couldn't resolve patient from family group for id: ${relatedPatientId}`
          );
        }

        if (relatedPatientRecord.data.is_head_of_family) {
          results.primaryContacts.push({
            primaryContact: {
              type: PatientRelationshipType.Unknown,
              patient: toNamedDocument(
                await Firestore.getDoc(
                  asDocRef<IBasePatient & IsPrimaryContact>(relatedPatientRef)
                )
              ),
            },
            patientRef: sourcePatientRef,
          });
          return;
        }

        // source is me and related is caia
        results.relationships.push({
          patientRef: sourcePatientRef,
          relation: {
            type: PatientRelationshipType.Unknown,
            patient: toNamedDocument(await Firestore.getDoc(relatedPatientRef)),
          },
          // isPrimaryContact: sourcePatientIsHead,
        });
      });

      return results;
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMapHandler: TranslationMapHandler,
    jobData: IPatientRelationshipJobData,
    migrationData: IPatientMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const patientRefs: DocumentReference<IPatient>[] = [];
      await resolveSequentially(
        migrationData.primaryContacts,
        async (relationship) => {
          await FirestoreMigrate.patchDoc(relationship.patientRef, {
            primaryContact: relationship.primaryContact,
          });

          await PatientRelationship.addPrimaryRelationship(
            await Firestore.getDoc(relationship.patientRef)
          );
          patientRefs.push(relationship.patientRef);
        }
      );
      await resolveSequentially(
        migrationData.relationships,
        async (relationship) => {
          const patient = await Firestore.getDoc(relationship.patientRef);
          Patient.upsertRelationship(patient, relationship.relation);
          await FirestoreMigrate.patchDoc(relationship.patientRef, {
            relationships: patient.relationships,
          });

          patientRefs.push(relationship.patientRef);
        }
      );
      return this._buildSuccessResponse(jobData.sourcePatient.record, [
        migrationData.patientRef,
        ...uniqWith(patientRefs, isSameRef),
      ]);
    } catch (error) {
      return this._buildErrorResponse(
        jobData.sourcePatient.record,
        getError(error)
      );
    }
  }

  private _buildSuccessResponse(
    sourcePatient: Pick<
      IGetRecordResponse<ExactPatient>['record'],
      'label' | 'uid' | 'id'
    >,
    patientRefs: DocumentReference<IPatient>[]
  ): IDestinationEntityRecord<IPatientRelationshipDestinationRecord> {
    return {
      uid: sourcePatient.uid,
      label: sourcePatient.label,
      data: {
        sourceRef: sourcePatient.id,
        patientRefs,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: sourcePatient.id,
      migratedAt: toTimestamp(),
    };
  }
}
